import React, { createContext, useContext } from 'react';
import { Box, Text } from '@mantine/core';
import type { 
  User, 
  Customer, 
  RecordingStatus, 
  AuthContextType 
} from './types';

/**
 * Authentication Context Module
 * 
 * @description
 * This module defines the Authentication Context used throughout the application.
 * It provides authentication state, user information, customer management,
 * and session recording functionality to all components in the application.
 * 
 * The auth context is the central hub for all authentication-related operations
 * and serves as a single source of truth for the application's authentication state.
 */

/**
 * Result interface for login operations
 * 
 * @property success - Whether the login attempt was successful
 * @property error - Optional error message if login failed
 * @property redirectTo - Optional redirect path after successful login
 */
export interface LoginResult {
  success: boolean;
  error?: string;
  redirectTo?: string;
}

/**
 * Result interface for customer selection operations
 * 
 * @property success - Whether the customer selection was successful
 * @property error - Optional error message if selection failed
 */
export interface SelectCustomerResult {
  success: boolean;
  error?: string;
}

/**
 * Extended authentication context type
 * 
 * @description
 * Defines the complete authentication context API available to components.
 * This interface extends the base AuthContextType with additional functionality
 * specific to this application's authentication requirements.
 */
export interface ExtendedAuthContextType {
  // Auth state
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  /**
   * The session could not be verified because the backend was unreachable —
   * the tokens are still there, so this is NOT signed out (#433). Guards show
   * a retry screen instead of redirecting to /login.
   */
  isUnreachable: boolean;
  /** A single re-check (Try again / background) is running. */
  isRechecking: boolean;
  /** Re-run the session check once (the retry screen's button). */
  retrySession: () => Promise<void>;
  /** Drop the tokens and go to the login form (the retry screen's way out). */
  signInAgain: () => void;
  
  // User and customer data
  user: User | null;
  customers: Customer[];
  activeCustomer: Customer | null;
  
  // Session recording
  recordingStatus: RecordingStatus;
  
  // Auth methods
  login: (
    email: string, 
    password: string, 
    recordSession?: boolean,
    redirectTo?: string
  ) => Promise<LoginResult>;
  
  logout: () => Promise<void>;
  
  // Customer methods
  selectCustomer: (customer: Customer) => Promise<SelectCustomerResult>;
  
  // Session recording methods
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;
}

// Create the context with default values
export const AuthContext = createContext<ExtendedAuthContextType>({
  isAuthenticated: false,
  isInitialized: false,
  isLoading: true,
  isRefreshing: false,
  isUnreachable: false,
  isRechecking: false,
  retrySession: async () => {},
  signInAgain: () => {},
  
  user: null,
  customers: [],
  activeCustomer: null,
  
  recordingStatus: { isRecording: false, isPaused: false },
  
  login: async (email: string, password: string, recordSession?: boolean, redirectTo?: string) => ({ 
    success: false, 
    error: 'AuthContext not initialized' 
  }),
  logout: async () => {},
  
  selectCustomer: async () => ({ success: false, error: 'AuthContext not initialized' }),
  
  startRecording: () => {},
  pauseRecording: () => {},
  resumeRecording: () => {},
  stopRecording: () => {},
});

/**
 * Custom hook for accessing the authentication context throughout the application
 * 
 * @description
 * Provides access to authentication state and methods from any component in the
 * application. Ensures that the hook is only used within an AuthProvider.
 * 
 * @example
 * ```tsx
 * import { useAuth } from '@/contexts/auth';
 * 
 * const MyComponent = () => {
 *   const { isAuthenticated, user, logout } = useAuth();
 *   
 *   return (
 *     <Box>
 *       {isAuthenticated ? (
 *         <>
 *           <Text>Welcome, {user?.first_name}!</Text>
 *           <button onClick={logout}>Logout</button>
 *         </>
 *       ) : (
 *         <Text>Please log in</Text>
 *       )}
 *     </Box>
 *   );
 * };
 * ```
 * 
 * @returns {ExtendedAuthContextType} The authentication context
 * @throws {Error} If used outside of an AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
